import type { GridMap } from "../grid/GridMap.js";
import type { Position } from "../types/grid.js";
import type { PlayerCharacter, Character } from "../types/entity.js";
import { hasLineOfSight } from "../grid/LineOfSight.js";
import { chebyshev } from "../grid/distance.js";
import { WorldStore } from "../../store/WorldStore.js";
import {
  highestPassivePerceiver,
  passivePerception,
} from "./SecretDoorPerception.js";

/**
 * Passive-Perception trap detector (W18 follow-up).
 *
 * Mirrors `SecretDoorPerception`: register every `trap` Tiled object on
 * map load, call `checkOnMove` from the InputHandler step callback. On
 * success, flips `WorldStore.markTrapRevealed` (which emits
 * `world:trap_revealed`) and the floor decal pops in.
 *
 * Range is intentionally tighter than secret doors — the spec frames
 * traps as "you have to walk close to spot them," whereas secret doors
 * can read at a corridor's distance.
 */
export const TRAP_PASSIVE_RANGE = 2;

export interface TrapRecord {
  readonly trapId: string;
  readonly position: Position;
  readonly perceptionDc: number;
}

export class TrapPerception {
  private readonly traps = new Map<string, TrapRecord>();

  constructor(private readonly grid: GridMap) {}

  /** Register a trap. Idempotent on `trapId`. */
  register(record: TrapRecord): void {
    this.traps.set(record.trapId, record);
  }

  /** Number of traps still hidden on the active map. */
  hiddenCount(): number {
    let n = 0;
    for (const id of this.traps.keys()) {
      if (!WorldStore.isTrapRevealed(id)) n += 1;
    }
    return n;
  }

  /** Drop all registrations (called on map teardown). */
  clear(): void {
    this.traps.clear();
  }

  /**
   * Roll passive Perception against every still-hidden trap within
   * `TRAP_PASSIVE_RANGE` cells with line-of-sight. Returns the trapIds
   * revealed by this call. Safe to call on every step.
   */
  checkOnMove(
    playerPos: Position,
    party: readonly (PlayerCharacter | Character)[],
  ): readonly string[] {
    if (this.traps.size === 0) return [];
    const perceiver = highestPassivePerceiver(party);
    if (!perceiver) return [];

    const passive = passivePerception(perceiver);
    const newlyRevealed: string[] = [];

    for (const trap of this.traps.values()) {
      if (WorldStore.isTrapRevealed(trap.trapId)) continue;
      if (chebyshev(playerPos, trap.position) > TRAP_PASSIVE_RANGE) continue;
      if (!hasLineOfSight(this.grid, playerPos, trap.position)) continue;
      if (passive < trap.perceptionDc) continue;

      WorldStore.markTrapRevealed(trap.trapId, trap.position);
      newlyRevealed.push(trap.trapId);
    }

    return newlyRevealed;
  }
}
